import React, { useState } from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";

import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

const PastEventsCarousel = ({ events = [] }) => {
  const navigate = useNavigate();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showNoVideoNotification, setShowNoVideoNotification] = useState(false);

  // First filter out any null/undefined events
  const validEvents = events.filter((event) => event != null);

  // Group events by date and sort (most recent first)
  const groupedEvents = validEvents.reduce((acc, event) => {
    const dateKey = event.dateEvent || event.date;

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);

    return acc;
  }, {});

  // Sort dates in descending order (most recent first)
  const sortedDates = Object.keys(groupedEvents).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  // Flatten events for carousel
  const carouselEvents = sortedDates.flatMap((date) => groupedEvents[date]);

  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    dragFree: true,
  });

  // Don't render if no valid events
  if (!validEvents || validEvents.length === 0) return null;

  const handleEventClick = (event) => {
    if (event.strVideo) {
      // Extract YouTube video ID from URL
      let videoId = event.strVideo;

      if (event.strVideo.includes("youtube.com/watch?v=")) {
        videoId = event.strVideo.split("v=")[1];
        const ampersandPosition = videoId.indexOf("&");

        if (ampersandPosition !== -1) {
          videoId = videoId.substring(0, ampersandPosition);
        }
      } else if (event.strVideo.includes("youtu.be/")) {
        videoId = event.strVideo.split("youtu.be/")[1];
        const queryPosition = videoId.indexOf("?");

        if (queryPosition !== -1) {
          videoId = videoId.substring(0, queryPosition);
        }
      }
      setSelectedVideo(videoId);
      setShowVideoModal(true);
    } else {
      // Show notification if no video available
      setShowNoVideoNotification(true);
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowNoVideoNotification(false);
      }, 3000);
    }
  };

  const renderEventCard = (event) => {
    // Safety check for undefined/null events
    if (!event) {
      return null;
    }

    // Parse UTC timestamp to local time
    let eventDate = null;

    if (event.strTimestamp) {
      // strTimestamp is ISO-8601 format (UTC) - it may or may not have Z suffix
      const timestampStr =
        event.strTimestamp.endsWith("Z") ||
        event.strTimestamp.includes("+") ||
        event.strTimestamp.includes("-")
          ? event.strTimestamp
          : `${event.strTimestamp}Z`;

      eventDate = new Date(timestampStr);
    } else if (event.timestamp != null) {
      if (typeof event.timestamp === "number") {
        const ms =
          event.timestamp < 1e12 ? event.timestamp * 1000 : event.timestamp;

        eventDate = new Date(ms);
      } else {
        const ts = String(event.timestamp);
        const timestampStr =
          ts.endsWith("Z") || ts.includes("+") || ts.includes("-")
            ? ts
            : `${ts}Z`;

        eventDate = new Date(timestampStr);
      }
    } else if (event.dateEvent && event.strTime) {
      // Fallback: combine dateEvent and strTime as UTC
      const combinedDateTime = `${event.dateEvent}T${event.strTime}Z`;

      eventDate = new Date(combinedDateTime);
    } else if (event.dateEvent) {
      // If only dateEvent is available, treat as UTC date at midnight
      eventDate = new Date(`${event.dateEvent}T00:00:00Z`);
    }

    // Format date for the pill
    const formatDateForPill = () => {
      if (!eventDate) return "";

      const today = new Date();
      const yesterday = new Date(today);

      yesterday.setDate(yesterday.getDate() - 1);

      const isYesterday = eventDate.toDateString() === yesterday.toDateString();
      const isToday = eventDate.toDateString() === today.toDateString();

      if (isYesterday) {
        return "YESTERDAY";
      } else if (isToday) {
        return "TODAY";
      } else {
        const daysDiff = Math.floor(
          (today - eventDate) / (1000 * 60 * 60 * 24)
        );

        if (daysDiff <= 7 && daysDiff > 0) {
          return eventDate
            .toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })
            .toUpperCase();
        } else {
          return eventDate
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
            .toUpperCase();
        }
      }
    };

    const homeTeam = event.homeTeam || event.strHomeTeam || "";
    const awayTeam = event.awayTeam || event.strAwayTeam || "";

    return (
      <div className="cursor-pointer group">
        <div
          className="relative rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105 aspect-video"
          role="button"
          style={{
            boxShadow:
              "0 15px 35px -15px rgba(0, 0, 0, 0.3), 0 10px 20px -10px rgba(0, 0, 0, 0.2)",
          }}
          tabIndex={0}
          onClick={() => handleEventClick(event)}
          onKeyDown={activateOnEnterOrSpace}
        >
          <img
            alt={
              event.strEvent || `${event.strHomeTeam} vs ${event.strAwayTeam}`
            }
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            src={
              event.posterPath ||
              event.strThumb ||
              event.poster ||
              event.backdropPath ||
              event.thumbnail ||
              event.strPostponed ||
              event.strSquare ||
              event.strBanner ||
              "/background.png"
            }
            onError={(e) => {
              // Try backdropPath as fallback if main image fails
              if (event.backdropPath && e.target.src !== event.backdropPath) {
                e.target.src = event.backdropPath;
              } else {
                e.target.src = "/background.png";
              }
            }}
          />

          {/* Date Pill - Top Left */}
          {eventDate && (
            <div className="absolute top-3 left-3 bg-gray-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full z-10">
              {formatDateForPill()}
            </div>
          )}

          {/* Play Icon - Center (only show if video is available) */}
          {event.strVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-black/60 rounded-full flex items-center justify-center group-hover:bg-black/80 group-hover:scale-110 transition-all duration-300">
                <i className="fas fa-play text-white text-3xl pl-1" />
              </div>
            </div>
          )}
        </div>

        {/* Team Names */}
        {homeTeam && awayTeam && (
          <div className="mt-2 px-1 overflow-hidden relative">
            <div className="text-white/80 text-sm truncate group-hover:opacity-0 hover:opacity-0 transition-opacity">
              <span className="font-bold">{homeTeam}</span> vs{" "}
              <span className="font-bold">{awayTeam}</span>
            </div>
            <div className="text-white/80 text-sm whitespace-nowrap absolute top-0 left-0 opacity-0 group-hover:opacity-100 hover:opacity-100 group-hover:animate-scroll-left hover:animate-scroll-left px-1">
              <span className="font-bold">{homeTeam}</span> vs{" "}
              <span className="font-bold">{awayTeam}</span>
              <span className="inline-block ml-8">
                <span className="font-bold">{homeTeam}</span> vs{" "}
                <span className="font-bold">{awayTeam}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleViewMoreClick = () => {
    // Navigate to the past events page
    navigate("/web-player/sports/events/past");
  };

  const renderViewMoreCard = () => (
    <div
      className="relative group cursor-pointer rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105"
      role="button"
      tabIndex={0}
      onClick={handleViewMoreClick}
      onKeyDown={activateOnEnterOrSpace}
    >
      <div className="aspect-video relative flex items-center justify-center">
        {/* Icon and Text */}
        <div className="text-center">
          <i className="fa-solid fa-rectangle-history text-6xl text-white/40 group-hover:text-white/60 transition-colors duration-300 mb-3" />
          <div className="text-white/80 text-lg font-semibold">View More</div>
          <div className="text-white/60 text-sm mt-1">
            Browse all past events
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="relative pb-6">
        <div className="overflow-hidden py-4 -mt-4 px-2" ref={emblaRef}>
          <div className="flex gap-4">
            {carouselEvents.map((event) => (
              <div
                className="flex-[0_0_18.18%] min-w-0"
                key={event.idEvent || event.id}
              >
                {renderEventCard(event)}
              </div>
            ))}
            {/* View More Card - Always show as last card */}
            <div className="flex-[0_0_18.18%] min-w-0" key="view-more-card">
              {renderViewMoreCard()}
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
          <div className="relative w-full max-w-6xl mx-4">
            <button
              className="absolute -top-12 right-0 text-white text-xl hover:text-gray-300 transition-colors"
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              <i className="fas fa-times" /> Close
            </button>
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1&rel=0&modestbranding=1`}
                title="YouTube video player"
              />
            </div>
          </div>
        </div>
      )}

      {/* No Video Notification */}
      {showNoVideoNotification && (
        <div className="fixed bottom-8 right-8 z-50 animate-fade-in">
          <div className="bg-gray-800 text-white px-6 py-4 rounded-lg shadow-xl flex items-center space-x-3">
            <i className="fas fa-video-slash text-xl" />
            <span className="text-lg">No video available for this event</span>
          </div>
        </div>
      )}
    </>
  );
};

export default PastEventsCarousel;
